import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useGetDatetimeQuery } from 'services';

dayjs.extend(utc);
dayjs.extend(timezone);

const SystemDatetimeContext = createContext<{ datetime: dayjs.Dayjs } | null>({ datetime: dayjs().tz('UTC') });

export const SystemDatetimeProvider = ({ children }: { children: ReactNode }) => {
  const { data: datetimeDto } = useGetDatetimeQuery(null, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true
  });

  const [{ datetime, timezone }, setDatetime] = useState<{ datetime: Date; timezone?: string }>({
    datetime: new Date(),
    timezone: 'UTC'
  });

  useEffect(() => {
    if (datetimeDto?.data) {
      setDatetime({
        datetime: datetimeDto.data.datetime,
        timezone: datetimeDto.data.timezone
      });
    }
  }, [datetimeDto]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDatetime(({ datetime, timezone }) => ({
        datetime: dayjs(datetime).add(1, 'second').toDate(),
        timezone: timezone
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <SystemDatetimeContext.Provider value={{ datetime: dayjs(datetime).tz(timezone) }}>
      {children}
    </SystemDatetimeContext.Provider>
  );
};

export const useSystemDatetime = () => {
  const context = useContext(SystemDatetimeContext);
  if (!context) {
    throw new Error('useSystemDatetime must be used within a SystemDatetimeProvider');
  }
  return context;
};
